import React, { useState, useRef } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { uploadConversationsFile } from '../services/api';
import { getInvalidFileTypeError, getUploadErrorInfo, type UploadErrorInfo } from '../utils/uploadErrors';
import { useAuth } from '../context/AuthContext';
import Auth from './Auth';
import UploadErrorModal from './UploadErrorModal';

const ConversationUpload: React.FC = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [uploadError, setUploadError] = useState<UploadErrorInfo | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [showAuth, setShowAuth] = useState(false);

  const uploadMutation = useMutation(uploadConversationsFile, {
    onSuccess: (data) => {
      setSelectedFile(null);
      queryClient.invalidateQueries(['conversations']);
      
      // Create detailed success message
      let message = `Successfully processed ${data.conversation_ids.length} conversation(s)!`;
      if (data.duplicates_skipped > 0) {
        message += `\n\n• ${data.new_conversations} new conversations added\n• ${data.duplicates_skipped} duplicates skipped`;
      } else {
        message += `\n\n• ${data.new_conversations} new conversations added`;
      }
      
      setSuccessMessage(message);
      setShowSuccess(true);
      
      // Auto-dismiss after 5 seconds (longer for duplicate info)
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    },
    onError: (error) => {
      console.error('Error uploading conversations:', error);
      setUploadError(getUploadErrorInfo(error));
    },
  });

  if (!user) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100vh - 4.5rem)',
        padding: '2rem',
        textAlign: 'center',
      }}>
        <h2 style={{
          color: '#ffffff',
          fontFamily: "'Inter', sans-serif",
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginBottom: '1rem',
        }}>
          Sign in to upload
        </h2>
        <p style={{
          color: 'rgba(255,255,255,0.8)',
          fontFamily: "'Inter', sans-serif",
          marginBottom: '1.5rem',
          maxWidth: '28rem',
        }}>
          Conversations are saved privately to your account in Firestore. Please log in or create an account to upload.
        </p>
        <button
          onClick={() => setShowAuth(true)}
          style={{
            background: '#ffffff',
            border: '1px solid #ffffff',
            color: '#000000',
            padding: '0.75rem 2rem',
            fontSize: '0.9rem',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            fontFamily: "'Inter', sans-serif",
            borderRadius: 0,
          }}
        >
          Login / Sign Up
        </button>
        {showAuth && <Auth onClose={() => setShowAuth(false)} />}
      </div>
    );
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    const jsonFile = files.find(file => file.type === 'application/json' || file.name.endsWith('.json'));
    
    if (jsonFile) {
      setSelectedFile(jsonFile);
    } else {
      setUploadError(getInvalidFileTypeError());
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        setSelectedFile(file);
      } else {
        setUploadError(getInvalidFileTypeError());
      }
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      uploadMutation.mutate(selectedFile);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      height: 'calc(100vh - 4.5rem)' // Full height minus navbar
    }}>
      {/* File Upload Area - Full Screen */}
      <div
        style={{
          flex: 1,
          background: isDragging 
            ? 'radial-gradient(circle at center, rgba(222, 184, 135, 0.15) 0%, transparent 100%)' 
            : 'transparent',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.4s ease',
          position: 'relative',
          boxShadow: isDragging ? 'inset 0 0 100px rgba(222, 184, 135, 0.1)' : 'none'
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div style={{ maxWidth: '600px', padding: '2rem' }}>
          <div className="mx-auto mb-8" style={{ 
            width: '8rem', 
            height: '8rem', 
            color: '#ffffff',
            filter: 'drop-shadow(0 0 30px rgba(222, 184, 135, 0.3))',
            transition: 'all 0.3s ease'
          }}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          
          <div>
            <p style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 700,
              color: '#ffffff',
              marginBottom: '1rem',
              fontFamily: "'Inter', sans-serif",
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              textShadow: '0 0 40px rgba(245, 245, 220, 0.15)'
            }}>
              Press Anywhere to Upload
            </p>
            <p style={{
              fontSize: 'clamp(1rem, 2vw, 1.25rem)',
              color: '#ffffff',
              marginTop: '1rem',
              fontFamily: "'Inter', sans-serif",
              letterSpacing: '0.02em',
              opacity: 0.8
            }}>
              Drag & drop your conversation JSON file or click to browse
            </p>
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {/* Selected File Overlay */}
      {selectedFile && (
        <div style={{
          position: 'absolute',
          bottom: '2rem',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '90%',
          maxWidth: '600px',
          backgroundColor: 'rgba(222, 184, 135, 0.1)',
          border: '2px solid #ffffff',
          borderRadius: 0,
          padding: '1.5rem',
          backdropFilter: 'blur(10px)'
        }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div style={{ width: '2rem', height: '2rem', color: '#2ecc71' }}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p style={{ 
                  fontWeight: 600, 
                  color: '#ffffff',
                  fontFamily: "'Inter', sans-serif",
                  letterSpacing: '0.05em'
                }}>
                  {selectedFile.name}
                </p>
                <p style={{ 
                  fontSize: '0.875rem', 
                  color: '#ffffff',
                  fontFamily: "'Inter', sans-serif"
                }}>
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveFile();
              }}
              style={{ color: '#e74c3c', background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleUpload();
            }}
            disabled={uploadMutation.isLoading}
            style={{
              width: '100%',
              background: uploadMutation.isLoading ? 'rgba(255, 255, 255, 0.5)' : '#ffffff',
              border: '1px solid #ffffff',
              color: '#000000',
              padding: '0.875rem 2rem',
              fontSize: '0.95rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              cursor: uploadMutation.isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: "'Inter', sans-serif",
              borderRadius: 0,
              textTransform: 'uppercase',
              opacity: uploadMutation.isLoading ? 0.6 : 1
            }}
          >
            {uploadMutation.isLoading ? 'Uploading...' : 'Upload Conversations'}
          </button>
        </div>
      )}

      <UploadErrorModal error={uploadError} onClose={() => setUploadError(null)} />

      {/* Success Notification */}
      {showSuccess && (
        <div style={{
          position: 'fixed',
          top: '6rem',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(46, 204, 113, 0.95)',
          border: '2px solid #2ecc71',
          borderRadius: 0,
          padding: '1.25rem 2.5rem',
          boxShadow: '0 10px 40px rgba(46, 204, 113, 0.4)',
          zIndex: 9999,
          animation: 'slideInFade 0.5s ease-out, slideOutFade 0.5s ease-in 2.5s',
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ 
              width: '2rem', 
              height: '2rem', 
              color: '#ffffff',
              animation: 'checkBounce 0.6s ease-out'
            }}>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div style={{
              color: '#ffffff',
              fontWeight: 600,
              fontSize: '1rem',
              fontFamily: "'Inter', sans-serif",
              letterSpacing: '0.05em',
              margin: 0,
              lineHeight: '1.4'
            }}>
              {successMessage.split('\n').map((line, index) => (
                <p key={index} style={{ margin: 0, fontSize: index === 0 ? '1rem' : '0.9rem', opacity: index === 0 ? 1 : 0.9 }}>
                  {line}
                </p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes slideInFade {
          0% {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
          100% {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        
        @keyframes slideOutFade {
          0% {
            opacity: 1;
            transform: translate(-50%, 0);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
        }

        @keyframes checkBounce {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.2);
          }
        }
      `}</style>
    </div>
  );
};

export default ConversationUpload;







